import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { FittingRoomContextState, FittingRoomProviderProps } from '../types'
import { standardSizeSort } from '@/utils'
import { useSearchParams } from '../../../../hooks/useSearchParams'

const FittingRoomContext = createContext<FittingRoomContextState>(
  {} as FittingRoomContextState,
)

export const FittingRoomProvider = ({
  children,
  data,
}: FittingRoomProviderProps) => {
  const { searchParams, replace } = useSearchParams()
  const [returnTo] = useState<string | null>(
    () => searchParams.get('return_to') || data?.return_to || '/',
  )

  useEffect(() => {
    if (searchParams.get('return_to')) {
      replace({ return_to: undefined })
    }
  }, [])

  const braSizes = useMemo(() => {
    const bandSizes = new Set<number>()
    const cupSizes = new Set<string>()
    const braletteSizes = new Set<string>()

    data?.bra_sizes?.forEach((size) => {
      bandSizes.add(Number(size.band_size))
      cupSizes.add(size.cup_size)
      braletteSizes.add(size.bralette_size)
    })

    return {
      bandSizes: Array.from(bandSizes).sort(),
      cupSizes: Array.from(cupSizes).sort(),
      braletteSizes: standardSizeSort(Array.from(braletteSizes)),
    }
  }, [])

  const bottomsSizes = useMemo(
    () => standardSizeSort(data?.bottoms_sizes ?? []),
    [],
  )

  const competitors = useMemo(
    () =>
      data.blocks
        .filter(({ type }) => type === 'competitor')
        .map(({ settings }) => ({
          name: settings.name,
          bandSizeModifier: settings.band_size_modifier,
          cupSizeModifier: settings.cup_size_modifier,
          bottomsSizeModifier: settings.bottoms_size_modifier,
        })),
    [],
  )

  const context: FittingRoomContextState = useMemo(
    () => ({
      returnTo: returnTo ?? '/',
      braSizes: data?.bra_sizes,
      bandSizes: braSizes.bandSizes,
      cupSizes: braSizes.cupSizes,
      braletteSizes: braSizes.braletteSizes,
      bottomsSizes,
      competitors,
      bandFitModifier: data?.band_fit_modifier,
      cupFitModifier: data?.cup_fit_modifier,
      hookFitModifier: data?.hook_fit_modifier,
      strapFitModifier: data?.strap_fit_modifier,
      maxBandSizeModifier: data?.max_band_size_modifier,
      maxCupSizeModifier: data?.max_cup_size_modifier,
      maxBottomsSizeModifier: data?.max_bottoms_size_modifier,
    }),
    [braSizes, bottomsSizes],
  )

  return (
    <FittingRoomContext.Provider value={context}>
      {children}
    </FittingRoomContext.Provider>
  )
}

export const useFittingRoomContext = () => useContext(FittingRoomContext)
